import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { FaArrowLeft, FaArrowRight, FaTrash, FaExternalLinkAlt, FaSync, FaExclamationTriangle } from 'react-icons/fa'
import { useParams, useNavigate, Link as RouterLink } from 'react-router'
import { Badge, Heading, Text, Box, Button, Icon, Input, Textarea, Select, Stack, Tag, FormControl, UserChip, UserChooser, SimpleTable, SimpleTabs, Tooltip, Loading, EmptyBox } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils'
import useStore from '../../store'
import useTitle from '../../hooks/useTitle'
import { FILE_TYPES, FILE_STATUSES } from './Files'

import NotFoundImage from '../../images/notFound.png'

export default function FileViewer () {
  const { id, fileId } = useParams()
  const navigate = useNavigate()
  const { user, trial, getTrialUsers } = useStore()
  const trialUsers = useMemo(() => getTrialUsers(id), [id])

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [approvals, setApprovals] = useState([])

  useTitle(file?.name || 'Document', trial)

  const loadFile = useCallback(() => {
    api.files.get(fileId, f => {
      setFile(f)
      setLoading(false)
      setNotFound(false)
    }, err => {
      setLoading(false)
      setNotFound(true)
      utils.toast('error', 'Unable to load file', err.message)
    })
  }, [fileId])

  useEffect(() => {
    setLoading(true)
    loadFile()
    api.files.listApprovalsForFile(fileId, d => setApprovals(d.approvals || []), () => {})
  }, [fileId])

  const canWrite = file && trial && utils.canWriteFile(user, trial, file)

  const persist = useCallback((data) => {
    return new Promise((resolve, reject) => {
      api.files.update(fileId, data, updated => {
        setFile(prev => ({ ...prev, ...updated }))
        resolve(updated)
      }, err => {
        utils.toast('error', 'Unable to update file', err.message)
        reject(err)
      })
    })
  }, [fileId])

  if (loading) return <Loading />
  if (notFound || !file) {
    return (
      <EmptyBox
        image={NotFoundImage}
        title='File not found'
        description='This file may have been deleted or you may not have permission to view it.'
      >
        <Button as={RouterLink} to={`/trials/${id}/files`}>Back to Document Control</Button>
      </EmptyBox>
    )
  }

  const lastVersion = file.latestVersion
  const statusMeta = FILE_STATUSES.find(s => s.key === file.status)

  return (
    <Stack gap={4} height='100%'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' gap={3}>
          <Button size='sm' variant='ghost' onClick={() => navigate(`/trials/${id}/files`)}>
            <Icon as={FaArrowLeft} /> Documents
          </Button>
          <Heading as='h2' size='md'>{file.name}</Heading>
        </Stack>
      </Stack>

      <Stack direction={{ base: 'column', lg: 'row' }} gap={4} alignItems='stretch' minH='70vh'>
        {/* Left: document preview */}
        <FilePreview
          fileName={file.fileName}
          downloadUrl={lastVersion?.url}
          previewKey={`${file._id}:${lastVersion?.storedName || ''}`}
          loadPreview={(ok, fail) => api.files.getPreview(file._id, ok, fail)}
          onRefresh={loadFile}
        />

        {/* Right: details */}
        <Stack flex={1} gap={4} minW={{ lg: '320px' }} maxW={{ lg: '380px' }}>
          <Box borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.surface' p={4}>
            <Stack gap={4} align='stretch'>
              <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap'>
                {file.currentVersion && statusMeta &&
                  <Badge colorPalette={statusMeta.colour}>{file.currentVersion} · {statusMeta.name}</Badge>}
                {lastVersion?.hash && <Tag size='sm' colorPalette='gray'><Text fontFamily='monospace'>{lastVersion.hash.slice(0, 8)}</Text></Tag>}
              </Stack>

              <TypeEditor type={file.type} canWrite={canWrite} onChange={type => persist({ type })} />

              <DescriptionEditor description={file.description} canWrite={canWrite} onSave={description => persist({ description })} />

              <TagsEditor tags={file.tags || []} canWrite={canWrite} onChange={tags => persist({ tags })} />

              <AssigneesEditor
                assignees={file.assignees || []}
                assigneeUsers={file.assigneeUsers || []}
                trialUsers={trialUsers}
                canWrite={canWrite}
                onChange={assignees => persist({ assignees })}
              />
            </Stack>
          </Box>

          <Box borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.surface' p={4}>
            <SimpleTabs
              lazyMount
              tabs={[
                { key: 'approvals', title: 'Approvals', content: <ApprovalsList approvals={approvals} /> },
                { key: 'versions', title: 'Versions', content: <VersionsList file={file} user={user} trial={trial} trialUsers={trialUsers} /> }
              ]}
            />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  )
}

function TypeEditor ({ type, canWrite, onChange }) {
  const current = FILE_TYPES.find(t => t.key === type)
  return (
    <FormControl label='Type'>
      {canWrite
        ? (
          <Select size='sm' value={type || ''} placeholder='Select a type...' onChange={e => onChange(e.target.value)}>
            {FILE_TYPES.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
          </Select>
          )
        : <Text>{current?.name || type || '—'}</Text>}
    </FormControl>
  )
}

// Reusable split-view left pane. Resolves a viewable URL via `loadPreview`
// (a (onSuccess, onError) => void the caller supplies, so the same component
// serves both the authenticated viewer and the token-gated approver flow):
// native types come back directly, Office types are converted to PDF on demand
// (and cached) server-side. A failed conversion is never cached, so "Try again"
// simply re-requests. The original file stays downloadable via `downloadUrl`.
//
// `previewKey` is a stable string identifying the preview target; the fetch
// re-runs only when it (or downloadUrl) changes — not on every render, so an
// inline `loadPreview` arrow is safe.
export function FilePreview ({ fileName, downloadUrl, loadPreview, onRefresh, previewKey, flex = 2 }) {
  const [state, setState] = useState({ loading: true, url: null, kind: null, error: null })
  const loadPreviewRef = useRef(loadPreview)
  loadPreviewRef.current = loadPreview

  const load = useCallback(() => {
    if (!downloadUrl) {
      setState({ loading: false, url: null, kind: 'none', error: null })
      return
    }
    setState({ loading: true, url: null, kind: null, error: null })
    loadPreviewRef.current(res => {
      setState({ loading: false, url: res.url || null, kind: res.kind, error: null })
    }, err => {
      setState({ loading: false, url: null, kind: null, error: err.message || 'Preview failed' })
    })
  }, [downloadUrl])

  useEffect(() => { load() }, [load, previewKey])

  function body () {
    if (!downloadUrl) {
      return <EmptyBox title='No version to preview' description='Upload a version of this file to preview it here.' />
    }
    if (state.loading) {
      return (
        <Stack alignItems='center' justifyContent='center' height='100%' minH='60vh' gap={3}>
          <Loading />
          <Text color='fg.subtle'>Preparing preview…</Text>
        </Stack>
      )
    }
    if (state.url) {
      return <iframe title={fileName} src={state.url} style={{ width: '100%', height: '100%', minHeight: '60vh', border: 'none' }} />
    }
    // Conversion failed, or the type isn't previewable — always offer the original.
    return (
      <Stack alignItems='center' justifyContent='center' height='100%' minH='60vh' gap={3} p={5}>
        <Text color='fg.subtle' textAlign='center'>
          {state.error
            ? "This document couldn't be prepared for preview."
            : "In-browser preview isn't available for this file type."}
        </Text>
        <Button as='a' href={downloadUrl} target='_blank' rel='noopener noreferrer' colorPalette='primary'>Download / open file <Icon as={FaExternalLinkAlt} /></Button>
        {state.error && <Button size='sm' variant='outline' onClick={load}>Try again</Button>}
      </Stack>
    )
  }

  return (
    <Box flex={flex} minW={0} borderWidth='1px' borderColor='border.default' rounded='md' overflow='hidden' bg='bg.surface' minH='70vh' display='flex' flexDirection='column'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' p={2} borderBottomWidth='1px' borderColor='border.default'>
        <Stack direction='row' alignItems='center' gap={2} minW={0}>
          <Text fontSize='sm' color='fg.subtle' truncate>{fileName || 'No file uploaded'}</Text>
          {state.kind === 'converted' &&
            <Tooltip content='This is an automatically generated preview. Complex documents may not display exactly as they do in their original application. You can download the file to view it as intended.'>
              <Box as='span' display='inline-flex' flexShrink={0} color='fg.muted' cursor='help'>
                <Icon as={FaExclamationTriangle} />
              </Box>
            </Tooltip>}
        </Stack>
        <Stack direction='row' gap={1}>
          {downloadUrl && <Button size='xs' variant='ghost' onClick={() => { if (onRefresh) onRefresh(); load() }} title='Refresh'><Icon as={FaSync} /></Button>}
          {downloadUrl && <Button size='xs' variant='ghost' as='a' href={downloadUrl} target='_blank' rel='noopener noreferrer' title='Open original in new tab'><Icon as={FaExternalLinkAlt} /></Button>}
        </Stack>
      </Stack>
      <Box flex={1} minH='60vh'>{body()}</Box>
    </Box>
  )
}

function DescriptionEditor ({ description, canWrite, onSave }) {
  const [value, setValue] = useState(description || '')
  const [saving, setSaving] = useState(false)
  useEffect(() => { setValue(description || '') }, [description])
  const dirty = value !== (description || '')

  function save () {
    setSaving(true)
    Promise.resolve(onSave(value)).finally(() => setSaving(false))
  }

  return (
    <FormControl label='Description'>
      {canWrite
        ? (
          <Stack gap={2} width='100%'>
            <Textarea width='100%' size='sm' placeholder='Add a short description for this document...' value={value} maxLength={500} onChange={e => setValue(e.target.value)} />
            {dirty && <Button size='xs' colorPalette='primary' alignSelf='start' isLoading={saving} onClick={save}>Save description</Button>}
          </Stack>
          )
        : <Text color={description ? 'fg.default' : 'fg.subtle'} whiteSpace='pre-line'>{description || 'No description.'}</Text>}
    </FormControl>
  )
}

function TagsEditor ({ tags, canWrite, onChange }) {
  const [input, setInput] = useState('')

  function addTag () {
    const t = input.trim()
    if (!t || tags.includes(t)) { setInput(''); return }
    onChange([...tags, t])
    setInput('')
  }

  return (
    <FormControl label='Tags'>
      <Stack gap={2} width='100%'>
        <Stack direction='row' flexWrap='wrap' gap={1}>
          {tags.length === 0 && <Text color='fg.subtle' fontSize='sm'>No tags.</Text>}
          {tags.map(t => (
            <Tag
              key={t} size='sm' colorPalette='gray' label={t}
              {...(canWrite && { onClose: () => onChange(tags.filter(x => x !== t)) })}
            />
          ))}
        </Stack>
        {canWrite &&
          <Stack direction='row' gap={2}>
            <Input
              size='sm' placeholder='Add a tag...' value={input} maxLength={50}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
            />
            <Button size='sm' variant='outline' onClick={addTag}>Add</Button>
          </Stack>}
      </Stack>
    </FormControl>
  )
}

function AssigneesEditor ({ assignees, assigneeUsers, trialUsers, canWrite, onChange }) {
  // Prefer hydrated assigneeUsers from the API; fall back to store lookup.
  function userFor (assigneeId) {
    return assigneeUsers.find(u => u._id === assigneeId) || trialUsers.find(u => u._id === assigneeId) || { _id: assigneeId }
  }

  return (
    <FormControl label='Assignees'>
      <Stack gap={3} width='100%'>
        <Stack direction='row' flexWrap='wrap' gap={2}>
          {assignees.length === 0 && <Text color='fg.subtle' fontSize='sm'>No one assigned.</Text>}
          {assignees.map(a => (
            <Stack direction='row' key={a} alignItems='center' gap={1} bg='bg.muted' borderRadius='md' pl={2} pr={canWrite ? 1 : 2} py={1}>
              <UserChip user={userFor(a)} compact />
              {canWrite && <Button size='2xs' variant='ghost' onClick={() => onChange(assignees.filter(x => x !== a))}><Icon as={FaTrash} /></Button>}
            </Stack>
          ))}
        </Stack>
        {canWrite &&
          <UserChooser
            size='sm' placeholder='Tap to assign...'
            users={trialUsers.filter(u => !assignees.includes(u._id))}
            onChosen={userIds => {
              const chosen = userIds[0]
              if (!chosen || assignees.includes(chosen)) return
              onChange([...assignees, chosen])
            }}
          />}
      </Stack>
    </FormControl>
  )
}

function ApprovalsList ({ approvals }) {
  if (!approvals || approvals.length === 0) {
    return <Text color='fg.subtle' fontSize='sm'>This file has not been submitted for approval yet.</Text>
  }
  return (
    <Stack gap={3}>
      {approvals.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(a => (
        <Stack key={a._id} bg='bg.muted' p={3} borderRadius='md' gap={2}>
          <Stack direction='row' alignItems='center' gap={2}>
            <Badge>{a.fileVersion}</Badge>
            {a.nextFileVersion && <Badge colorPalette='blue'><FaArrowRight /> {a.nextFileVersion}</Badge>}
            <Badge colorPalette={FILE_STATUSES.find(s => s.key === a.status)?.colour}>{a.status}</Badge>
          </Stack>
          <SimpleTable
            size='sm'
            headers={['Approver', 'Status']}
            rows={a.approvers?.map(approver => [
              <Text key={`${approver.id}-email`} fontSize='sm'>{approver.email}</Text>,
              <Badge key={`${approver.id}-status`} colorPalette={FILE_STATUSES.find(s => s.key === approver.status)?.colour}>{approver.status || 'pending'}</Badge>
            ])}
          />
        </Stack>
      ))}
    </Stack>
  )
}

// Version history for the Versions tab. Fetched lazily (the tab is lazyMounted)
// and gated on the same permission the standalone version viewer uses, so no
// new API surface is needed — listVersionsForFile already enforces it server-side.
function VersionsList ({ file, user, trial, trialUsers }) {
  const [versions, setVersions] = useState(null)
  const canRead = utils.canReadFileVersions(user, trial, file)

  useEffect(() => {
    if (!canRead) return
    setVersions(null)
    api.files.listVersionsForFile(file._id, d => setVersions(d.versions || []), err => {
      utils.toast('error', 'Unable to list versions', err.message)
      setVersions([])
    })
  }, [file._id, canRead])

  if (!canRead) return <Text color='fg.subtle' fontSize='sm'>You don't have permission to view file versions.</Text>
  if (versions === null) return <Loading />
  if (versions.length === 0) return <Text color='fg.subtle' fontSize='sm'>No versions yet.</Text>

  return (
    <SimpleTable
      size='sm'
      headers={['Version', 'Status', 'Hash', '']}
      rows={versions.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(v => {
        const createdBy = trialUsers?.find(u => u._id === v.createdBy) || { _id: v.createdBy }
        return [
          <Stack key={`${v.version}-version`} gap={1}>
            <Text fontFamily='monospace' fontWeight='semibold'>{v.version}</Text>
            <Stack direction='row' alignItems='center' gap={1}>
              <UserChip onlyAvatar compact user={createdBy} />
              <Text fontSize='xs' color='fg.subtle'>{utils.formatDateTime(v.createdAt)}</Text>
            </Stack>
          </Stack>,
          <Badge key={`${v.version}-status`} colorPalette={FILE_STATUSES.find(s => s.key === v.status)?.colour}>{v.status}</Badge>,
          v.hash
            ? <Text key={`${v.version}-hash`} fontFamily='monospace' fontSize='xs' wordBreak='break-all' title={v.hash}>{v.hash.slice(0, 8)}</Text>
            : <Text key={`${v.version}-hash`} color='fg.subtle'>—</Text>,
          v.url
            ? <Button key={`${v.version}-view`} size='2xs' variant='ghost' as='a' href={v.url} target='_blank' rel='noopener noreferrer' title='View this version'><Icon as={FaExternalLinkAlt} /></Button>
            : null
        ]
      })}
    />
  )
}
